import React from 'react';
import TurnstileWidget from './TurnstileWidget';
import RecaptchaWidget from './RecaptchaWidget';
import { 
  IHumanVerificationProps, 
  IVerifyHumanResponse 
} from '../../../types/humanVerification';
import { trackExceptionWithContext } from '../../../services/application-insights/appInsights';
import { AppInsightsComponents, AppInsightsErrorTypes } from '../../../services/application-insights/appInsightsConstants';

interface CombinedIHumanVerificationProps extends Omit<IHumanVerificationProps, 'onVerified'> {
  siteKey: string;
  version?: 'v2' | 'v3'; // for reCAPTCHA
  tabIndex?: number; // for reCAPTCHA
  onVerified: (token: string, result?: IVerifyHumanResponse) => void;
  onExpired?: () => void;
  onLoad?: () => void;
}

export const HumanVerificationWidget: React.FC<CombinedIHumanVerificationProps> = ({
  provider,
  siteKey,
  version = 'v2',
  action,
  minScore,
  appearance = 'always',
  size = 'normal',
  tabIndex,
  onVerified,
  onError,
  onExpired,
  onLoad,
  className,
  disabled,
  ...props
}) => {
  if (provider === 'turnstile') {
    return (
      <TurnstileWidget
        siteKey={siteKey}
        action={action}
        size={size}
        appearance='always'
        onVerified={onVerified}
        onError={onError}
        onExpired={onExpired}
        onLoad={onLoad}
        className={className}
        style={disabled ? { opacity: 0.5, pointerEvents: 'none' } : undefined}
        {...props}
      />
    );
  }

  if (provider === 'recaptcha') {
    return (
      <RecaptchaWidget
        siteKey={siteKey}
        version={version}
        action={action}
        minScore={minScore}
        size={size}
        tabIndex={tabIndex}
        onVerified={onVerified}
        onError={onError}
        onExpired={onExpired}
        onLoad={onLoad}
        className={className}
        style={disabled ? { opacity: 0.5, pointerEvents: 'none' } : undefined}
        {...props}
      />
    );
  }

  // Log unsupported provider error
  const errorMessage = `Unsupported verification provider: ${provider}`;
  trackExceptionWithContext(new Error(errorMessage), {
    component: AppInsightsComponents.HUMAN_VERIFICATION_WIDGET,
    action: 'unsupported-provider',
    additionalData: {
      errorType: AppInsightsErrorTypes.HUMAN_VERIFICATION_UNSUPPORTED_PROVIDER_ERROR,
      provider,
      siteKey: siteKey?.substring(0, 10) + '...',
      version,
      action
    }
  });

  return (
    <div className="human-verification-error">
      <p className="text-red-500">
        Unsupported verification provider: {provider}
      </p>
    </div>
  );
};

export default HumanVerificationWidget;